import React from 'react'

import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'

import { ZariContext } from '_zari/context'

import { Col, Form, Row, Divider, Button } from '_zari/core'
import Yup from '_zari/validation/_yup'

import { CodigosTransaccionesConstant } from 'constants/index'

import { IdGen } from 'utils'

import * as ActivoFijoService from 'services/activosFijos.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

export const ValidationSchema = Yup.object().shape({
  codigo: Yup.string().required(),
  descripcion: Yup.string()
    .max(100)
    .required(),
  cuentaContable: Yup.string().required(),
  depreciacionAnual: Yup.number()
    .max(100)
    .notRequired(),
  fechaUso: Yup.string().required(),
  fechaAdquisicion: Yup.string().required()
})

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      return [
        ...state,
        {
          ...action.payload,
          _idFila: IdGen.newId()
        }
      ]
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

const MainForm = ({ editData }) => {
  const __zariContext = React.useContext(ZariContext)
  const history = useHistory()

  const [detalles, detallesDispatcher] = React.useReducer(
    commonReducer,
    editData.detalles,
    detalles => {
      return detalles.map(x => ({ ...x, _idFila: IdGen.newId() }))
    }
  )

  const mainFormRef = React.useRef(null)

  const formInitialValues = React.useMemo(() => {
    return {
      codigo: editData.codigo || '',
      cuentaContable: editData.cuentaContable || '',
      cuentaContableInfo: editData.cuentaContableInfo || '',
      depreciacionAnual: editData.depreciacionAnual !== null ? editData.depreciacionAnual : '',
      descripcion: editData.descripcion || '',
      _idProducto: editData._idProducto || '',
      _idProductoPresentacion: editData._idProductoPresentacion || '',
      productoInfo: editData.productoInfo || '',
      _idCategoria: editData._idCategoria || '',
      categoria: editData.categoria || '',
      _idFamilia: editData._idFamilia || '',
      familia: editData.familia || '',
      _idSubFamilia: editData._idSubFamilia || '',
      subFamilia: editData.subFamilia || '',
      tipoActivoFijoSunat: editData.tipoActivoFijoSunat || '',
      estadoActivoFijoSunat: editData.estadoActivoFijoSunat || '',
      fechaAdquisicion: editData.fechaAdquisicion || '',
      fechaEntrega: editData.fechaEntrega || '',
      fechaUso: editData.fechaUso || '',
      marca: editData.marca || '',
      metodoDepreciacion: editData.metodoDepreciacion || '',
      modelo: editData.modelo || '',
      numeroAutorizacion: editData.numeroAutorizacion || '',
      serie: editData.serie || '',
      _idOficina: editData._idOficina || '',
      oficina: editData.oficina || '',
      valorAdquisicion: editData.valorAdquisicion !== null ? editData.valorAdquisicion : ''
    }
  }, [editData])

  const clickSubmit = React.useCallback(() => {
    mainFormRef.current.handleSubmit()
  }, [])

  const onSubmit = React.useCallback(
    formData => {
      const submitData = {
        ...formData,
        detalles: detalles
      }

      ActivoFijoService.actualizar(editData._id, submitData)
        .then(() => {
          __zariContext.snack('success', 'El registro se ha actualizado.')
          history.push(`/${CodigosTransaccionesConstant.ACTIVOSFIJOS_LISTA}`)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext, history, detalles, editData._id]
  )

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        innerRef={mainFormRef}
        onSubmit={onSubmit}
        validationSchema={ValidationSchema}
      >
        {({ errors, touched, values, setValues, isSubmitting, handleChange, setFieldValue }) => (
          <Form>
            <Row>
              <Col md={4}>
                <FormGroups.CodigoFormGroup iValue={values.codigo} />
                <FormGroups.ProductoFormGroup
                  fIsSubmitting={isSubmitting}
                  iValue={values.productoInfo}
                  setFieldValue={setFieldValue}
                />
                <FormGroups.DescripcionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.descripcion}
                  iTouched={touched.descripcion}
                />
                <FormGroups.CuentaContableFormGroup
                  setFieldValue={setFieldValue}
                  iError={errors.cuentaContable}
                  iTouched={touched.cuentaContable}
                  iValue={values.cuentaContableInfo}
                />
                <FormGroups.DepreciacionAnualFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.depreciacionAnual}
                  iTouched={touched.depreciacionAnual}
                />
              </Col>
              <Col md={3}>
                <FormGroups.CategoriaFormGroup fIsSubmitting={isSubmitting} />
                <FormGroups.FamiliaFormGroup fIsSubmitting={isSubmitting} />
                <FormGroups.SubFamiliaFormGroup fIsSubmitting={isSubmitting} />
                <FormGroups.ModeloFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.modelo}
                  iTouched={touched.modelo}
                />
                <FormGroups.SerieFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.serie}
                  iTouched={touched.serie}
                />
                <FormGroups.MarcaFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.marca}
                  iTouched={touched.marca}
                />
                <FormGroups.MetodoDepreciacionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.metodoDepreciacion}
                  iTouched={touched.metodoDepreciacion}
                />
                <FormGroups.TipoActivoFijoSunatFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.tipoActivoFijoSunat}
                  iTouched={touched.tipoActivoFijoSunat}
                />
                <FormGroups.EstadoActivoFijoSunatFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.estadoActivoFijoSunat}
                  iTouched={touched.estadoActivoFijoSunat}
                />
              </Col>
              <Col md={3}>
                <FormGroups.NumeroAutorizacionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.numeroAutorizacion}
                  iTouched={touched.numeroAutorizacion}
                />
                <FormGroups.FechaUsoFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaUso}
                  iTouched={touched.fechaUso}
                />
                <FormGroups.FechaAdquisicionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaAdquisicion}
                  iTouched={touched.fechaAdquisicion}
                />
              </Col>
              <Col md={2}>
                <FormGroups.OficinaFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors._idOficina}
                  iTouched={touched._idOficina}
                  setFieldValue={setFieldValue}
                />
                <FormGroups.FechaEntregaFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaEntrega}
                  iTouched={touched.fechaEntrega}
                />
                <FormGroups.ValorAdquisicionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.valorAdquisicion}
                  iTouched={touched.valorAdquisicion}
                />
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      <Divider noMargin />
      <Sections.DetallesCabeceraSection dispatch={detallesDispatcher} />
      <Divider noMargin />
      <Sections.DetallesTablaSection detalles={detalles} dispatch={detallesDispatcher} />
      <Divider noMargin />
      <Row>
        <Col md={12} className='text-right'>
          <Button onClick={clickSubmit}>Procesar</Button>
        </Col>
      </Row>
    </>
  )
}

export default React.memo(MainForm)
